/**
 * @file src/helpers/ory/getOryFailReason.ts
 * @exports getOryErrorMessageId, getOryFailReason, getOryFlowFailReason
 * @summary Classifies a failed Ory flow submission into a bounded analytics reason, from Ory's message IDs and status.
 * @remarks Ory message text can echo the user's input, so it is never reported. The one text comparison is against
 *          Kratos' fixed reason for exceeding its code-submission cap, which carries only a generic message ID.
 *          Codes and flows share a one-hour lifespan and a code is sent after its flow starts, so an expired code
 *          surfaces as the flow's 410 (`flow_expired`), never as an invalid code.
 */

import type { UiNode, UiText } from "@ory/kratos-client";
import { ORY_MESSAGE_MAPPING } from "src/const";
import type { AnalyticsAuthFailReason } from "../analyticsEvents";
import { getRequestFailReason, getResponseStatus } from "../getRequestFailReason";

type OryFlowWithUi = { ui?: { messages?: UiText[]; nodes?: UiNode[] } };

type OryFlowFailReason = Extract<
	AnalyticsAuthFailReason,
	"code_already_used" | "flow_rejected" | "invalid_code" | "too_many_attempts"
>;

const INVALID_CODE_MESSAGE_IDS: number[] = [
	ORY_MESSAGE_MAPPING.LOGIN_CODE_INVALID,
	ORY_MESSAGE_MAPPING.REGISTRATION_CODE_INVALID,
	ORY_MESSAGE_MAPPING.VERIFICATION_CODE_INVALID,
];

const TOO_MANY_ATTEMPTS_TEXT = "The request was submitted too often.";

const isTooManyAttempts = (message: UiText) =>
	message.id === ORY_MESSAGE_MAPPING.GENERIC_VALIDATION_ERROR &&
	typeof message.text === "string" &&
	message.text.startsWith(TOO_MANY_ATTEMPTS_TEXT);

const errorMessages = (flow: unknown): UiText[] => {
	const ui = (flow as OryFlowWithUi | undefined)?.ui;
	return [...(ui?.messages ?? []), ...(ui?.nodes ?? []).flatMap((node) => node.messages ?? [])].filter(
		(message) => message.type === "error",
	);
};

/** The flow from an Axios response, or the flow itself when a hook rethrew it. */
const flowOf = (error: unknown): unknown =>
	(error as { response?: { data?: unknown } } | undefined)?.response?.data ?? error;

/** The reason a flow's error messages describe, or undefined when the flow carries no error message. */
export const getOryFlowFailReason = (flow: unknown): OryFlowFailReason | undefined => {
	const messages = errorMessages(flow);
	if (!messages.length) return undefined;
	const ids = messages.map((message) => message.id);
	if (ids.some((id) => INVALID_CODE_MESSAGE_IDS.includes(id))) return "invalid_code";
	if (ids.includes(ORY_MESSAGE_MAPPING.REQUEST_ALREADY_COMPLETED)) return "code_already_used";
	if (messages.some(isTooManyAttempts)) return "too_many_attempts";
	return "flow_rejected";
};

/**
 * The reason for an error thrown by an Ory flow submission. A 410, or the expired-flow error body a hook rethrew,
 * is `flow_expired`; otherwise the flow's messages decide, then the transport classification.
 */
export const getOryFailReason = (error: unknown): AnalyticsAuthFailReason => {
	const flow = flowOf(error);
	const errorId = (flow as { error?: { id?: unknown } } | undefined)?.error?.id;
	if (getResponseStatus(error) === 410 || errorId === "self_service_flow_expired") return "flow_expired";
	return getOryFlowFailReason(flow) ?? getRequestFailReason(error);
};

/** Ory's ID for the first error message on a rejected flow. */
export const getOryErrorMessageId = (error: unknown): number | undefined => errorMessages(flowOf(error))[0]?.id;
